import { useCallback, useMemo, useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuShortcut,
} from '@/components/ui/context-menu';
import { useWorkflowStore } from '@/stores/workflow-store';
import { toast } from 'sonner';
import { getShortcuts } from '@/utils/keyboard';
import { Node } from 'reactflow';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EnhancedNodePalette } from './EnhancedNodePalette';
import { ALL_NODE_DEFINITIONS } from '@shared/nodes';
import { createPortal } from 'react-dom';

interface EdgeContextMenuPortalProps {
  edgeId: string | null;
  position: { x: number; y: number } | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EdgeContextMenuPortal({ 
  edgeId, 
  position, 
  isOpen, 
  onOpenChange 
}: EdgeContextMenuPortalProps) {
  const { nodes, edges, deleteEdge, addNode, addEdge } = useWorkflowStore();
  const [showNodePicker, setShowNodePicker] = useState(false);
  
  // Get shortcuts at runtime for proper OS detection
  const shortcuts = useMemo(() => getShortcuts(), []);

  const edge = edges.find((e) => e.id === edgeId);

  const handleDelete = useCallback(() => {
    if (edgeId) {
      deleteEdge(edgeId);
      toast.success('Connection deleted');
      onOpenChange(false);
    }
  }, [edgeId, deleteEdge, onOpenChange]);

  const handleInsertNode = useCallback(() => {
    setShowNodePicker(true);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleNodeSelect = useCallback((nodeType: string) => {
    if (!edge) return;

    // Find source and target nodes
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (!sourceNode || !targetNode) {
      toast.error('Could not find connected nodes');
      return;
    }

    // Calculate position at midpoint between source and target
    const midX = (sourceNode.position.x + targetNode.position.x) / 2;
    const midY = (sourceNode.position.y + targetNode.position.y) / 2;

    // Get node definition
    const nodeDefinition = ALL_NODE_DEFINITIONS[nodeType];
    
    // Determine node size
    const isComplexNode = nodeDefinition?.category === 'triggers' || 
                         nodeDefinition?.category === 'ai';
    const nodeWidth = isComplexNode ? 128 : 96;
    const nodeHeight = isComplexNode ? 128 : 96;

    // Create new node
    const newNode: Node = {
      id: `${nodeType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: nodeType,
      position: { x: midX, y: midY },
      data: {
        label: nodeDefinition?.name || nodeType,
        category: nodeDefinition?.category || 'action',
        nodeType: nodeType,
        parameters: {},
      },
      width: nodeWidth,
      height: nodeHeight,
    };

    // Delete the original edge
    deleteEdge(edgeId!);

    // Add the new node
    addNode(newNode);

    // Create edges: source -> newNode -> target
    const edge1 = {
      id: `${edge.source}-${newNode.id}`,
      source: edge.source,
      target: newNode.id,
      sourceHandle: edge.sourceHandle,
      targetHandle: 'default',
      type: 'custom',
    };

    const edge2 = {
      id: `${newNode.id}-${edge.target}`,
      source: newNode.id,
      target: edge.target,
      sourceHandle: 'default',
      targetHandle: edge.targetHandle,
      type: 'custom',
    };

    addEdge(edge1);
    addEdge(edge2);

    setShowNodePicker(false);
    toast.success(`Inserted ${nodeDefinition?.name || nodeType} node`);
  }, [edge, nodes, deleteEdge, addNode, addEdge, edgeId]);

  if (!isOpen || !position || !edgeId) return null;

  return (
    <>
      {createPortal(
        <div
          className="fixed z-50"
          style={{
            left: position.x,
            top: position.y,
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="bg-popover border border-border rounded-md shadow-md py-1 min-w-[12rem]">
            <button
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
              onClick={handleInsertNode}
            >
              <Plus className="h-4 w-4" />
              Insert Node
            </button>
            <button
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground flex items-center gap-2 text-red-600"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" />
              Delete
              <span className="ml-auto text-xs text-muted-foreground">{shortcuts.delete}</span>
            </button>
          </div>
        </div>,
        document.body
      )}

      <Dialog open={showNodePicker} onOpenChange={setShowNodePicker}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Insert Node</DialogTitle>
          </DialogHeader>
          <div className="h-[60vh]">
            <EnhancedNodePalette 
              onNodeSelect={handleNodeSelect}
              compact={true}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
